import {
  useCapTableTransactions,
  useCompanyData,
  useGetWorkRequestsByCompany,
  useStockClasses,
  useJurisdictions,
} from '@commenda/api';
import { IncorporationRequestDataType } from '@commenda/types';
import { useGetAllDirectorsInCompany } from '@commenda/api/director';
import { EditIcon01, ProceedIcon } from '@commenda/icons';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
  Button,
} from '@commenda/kakashi';
import { YesNoNotSureOptions } from '@commenda/types/src/common/enums';
import { TEquityInfoKS } from '@commenda/types/src/incorporation/types';
import { TSetNotification } from '@commenda/types/src/random';
import { WorkRequestResponseType } from '@commenda/types/src/work-request/types';
import {
  fetcher,
  getCorporationNameFromWorkRequest,
  HIRE_PLAN_LABELS,
  NonTimezoneSensitiveDate,
} from '@commenda/utils';
import {
  AddressType,
  CorporationType,
  IncorporationRequestStatus,
  LegacyDocumentType,
} from '@prisma/client';
import { Country, State } from 'country-state-city';
import { useRouter } from 'next/router';
import { useEffect, useMemo, useState } from 'react';
import { KeyedMutator } from 'swr';
import { USResponsibleParty } from '../../../service-request/US_INCORP/common/summary/responsible-party';
import {
  IncorporationFormHeading,
  SubmitWorkRequestModalKS,
  SummaryFieldKS,
} from '../common';
import CorporateShareholdersSummary from '../common/CorporateShareholdersSummary';
import {
  calculateStakeholdersEquityMap,
  extractCorporateShareholders,
} from '../common/corporateStakeholder/utils';
import CapTable from '../EquityInfo/CapTable';
import { CompanyValuationSummary } from '../EquityInfo/CompanyValuationSummary';
import ShareCapitalSummary from '../EquityInfo/ShareCapitalSummary';
import {
  TCapTableDataItem,
  TShareholderVsSharePercentageMap,
} from '../EquityInfo/types';
import { generateOwnershipSummaryTableRowsForUSEntities } from './EquityInfo/utils';
import USFounderField from './USFounderField';

const USCorporateStakeholderSummarySection = ({
  setActiveStep,
  onProceed,
  data,
  mutate,
  hideActionButtons = false,
  isReviewSummary = false,
  setNotification,
  token,
  corporationId,
  companyId,
}: {
  setActiveStep: (stepIdx: number) => void;
  onProceed: (idx: number) => void;
  data: WorkRequestResponseType & { requestData: IncorporationRequestDataType };
  mutate: KeyedMutator<unknown>;
  hideActionButtons?: boolean;
  isReviewSummary?: boolean;
  token: string;
  setNotification: TSetNotification;
  corporationId: number;
  companyId: number;
}) => {
  const { mutate: mutateCompanyData } = useCompanyData(companyId);
  const { labelOf } = useJurisdictions();
  const { mutate: mutateWorkRequests } = useGetWorkRequestsByCompany(companyId);
  const { data: directorsData } = useGetAllDirectorsInCompany(companyId);

  const responsibleParty = directorsData?.directors?.find((director) => {
    return data?.corporation?.corporationHasDirector?.some(
      (corpHasDir) =>
        corpHasDir.directorId === director.id &&
        corpHasDir.additionalDirectorInformation?.isResponsibleParty
    );
  });

  const router = useRouter();

  const { data: capTableTransactions } = useCapTableTransactions(corporationId);

  const { data: stockClasses } = useStockClasses(corporationId);

  // Get individual shareholders from cap table transactions
  const individualShareholders = useMemo(() => {
    if (!capTableTransactions || !directorsData?.directors) return [];

    // Get director IDs from cap table transactions
    const directorIdsFromCapTable = capTableTransactions
      .map((tx) => {
        const director = tx.stakeholding?.stakeholder?.director;
        if (!director) return null; // skip corporate shareholders
        return director.id;
      })
      .filter(Boolean);

    // Get full director data from directorsData
    const shareholders = directorsData.directors.filter((director) =>
      directorIdsFromCapTable.includes(director.id)
    );

    return shareholders;
  }, [capTableTransactions, directorsData]);

  const shareholderDirectorIds = useMemo(
    () => new Set(individualShareholders.map((shareholder) => shareholder.id)),
    [individualShareholders]
  );

  const [equityInfo, setEquityInfo] = useState<TEquityInfoKS>({
    shareCount: null,
    price: null,
    shareRetained: null,
  });

  const [ownershipSummaryTableData, setOwnershipSummaryTableData] = useState<
    TCapTableDataItem[]
  >([]);

  const [hasESOPs, setHasESOPs] = useState(false);

  const [selectedStakeholders, setSelectedStakeholders] =
    useState<TShareholderVsSharePercentageMap>();

  // we can pick the first stock class here on the corporation level, as when the incorporation process is ongoing
  // we will only have one stock class
  const stockClass = stockClasses?.[0];

  const isCorporateSubsidiary = false;

  const { id } = router.query as { id: string };

  const [isSubmitting, setSubmitting] = useState(false);

  const physicalAddress = data?.addresses?.find(
    (address) => address.type === AddressType.OperatingAddress
  )?.value;

  const mailingAddress = data?.addresses?.find(
    (address) => address.type === AddressType.MailingAddress
  )?.value;

  const hirePlan = data?.requestData?.planToHireUSEmployeesInFirst12Months;

  const getOwnershipSummaryTableForUSEntities = () => {
    const ownershipSummaryTableRows =
      generateOwnershipSummaryTableRowsForUSEntities({
        equityInfo,
        selectedStakeholders,
        corporationType: data?.requestData?.corporationType,
        hasESOPs,
      });

    setOwnershipSummaryTableData(ownershipSummaryTableRows);
  };

  useEffect(() => {
    getOwnershipSummaryTableForUSEntities();
  }, [equityInfo, hasESOPs, selectedStakeholders, capTableTransactions]);

  useEffect(() => {
    if (!stockClass) return;
    if (!capTableTransactions) return;

    const { shareRetained, stakeholdersEquityMap, authorizedShares } =
      calculateStakeholdersEquityMap({
        capTableTransactions,
        stockClass,
      });

    setEquityInfo({
      shareCount: authorizedShares || 10_000_000,
      price:
        stockClass?.additionalInformation?.pricePerShare?.amount || 0.00001,
      shareRetained,
    });

    setHasESOPs(shareRetained > 0 && capTableTransactions?.length > 0);

    if (capTableTransactions?.length > 0)
      setSelectedStakeholders(stakeholdersEquityMap);
  }, [data, capTableTransactions, stockClass]);

  const onSubmit = async () => {
    setSubmitting(true);

    const capTableSnapshot = { capTableTransactions, stockClass };

    const response = await fetcher(
      `workRequest/${id}`,
      {
        method: 'POST',
        body: {
          status: IncorporationRequestStatus.Submitted,
          requestData: {
            capTableSnapshot,
          },
        },
      },
      token
    );

    if (!response.ok) {
      setNotification({
        show: true,
        valid: false,
        title: 'Error',
        message:
          'Something went wrong, please try again or contact Team Commenda if you need help.',
      });
      setSubmitting(false);
      return;
    }

    setSubmitting(false);
    await mutateWorkRequests();
    await mutateCompanyData();
    router.back();
  };

  return (
    <div className="flex flex-col gap-8 max-w-[700px]">
      <IncorporationFormHeading
        text="Summary"
        additionalText="Please verify the following information before submitting. Once submitted, you may not be able to make changes."
      />
      <div className="flex flex-col gap-3">
        <div className="flex items-center justify-between">
          <h4
            className="body-16-bold text-text-primary
          "
          >
            Company Info
          </h4>
          {hideActionButtons ? null : (
            <EditIcon01
              className="cursor-pointer"
              onClick={() => setActiveStep(0)}
            />
          )}
        </div>
        <div className="flex flex-col gap-3 bg-bg-disabled-subtle rounded-sm">
          <div className="flex flex-col gap-2 p-6">
            <SummaryFieldKS
              heading="Name"
              value={getCorporationNameFromWorkRequest(data?.requestData)}
            />
            {(data?.requestData?.companyLegalName2 ||
              data?.corporation?.corporationType === CorporationType.LLC) && (
              <SummaryFieldKS
                heading="Fallback Name"
                value={
                  getCorporationNameFromWorkRequest(
                    data?.requestData,
                    'companyLegalName2'
                  ) || 'Not provided'
                }
              />
            )}
            <SummaryFieldKS
              heading="Corporation Type"
              value={
                data?.corporation?.corporationType === CorporationType.LLC
                  ? 'LLC'
                  : 'C Corp'
              }
            />
            <SummaryFieldKS
              heading="Jurisdiction"
              value={
                !data?.requestData?.incorporationJurisdiction
                  ? 'Not sure'
                  : labelOf(data.requestData.incorporationJurisdiction)
              }
            />
            <SummaryFieldKS
              heading="Description"
              value={data?.requestData?.businessDescription}
            />
            <SummaryFieldKS
              heading="Company Website"
              value={data?.requestData?.socialLink}
            />
            <SummaryFieldKS
              heading="Plan to hire US employees in first 12 months"
              value={HIRE_PLAN_LABELS[hirePlan] || "I'm not sure"}
            />
            {hirePlan === YesNoNotSureOptions.Yes ? (
              <SummaryFieldKS
                heading="Employees planned in first year"
                value={data?.requestData?.numberOfEmployees ?? 'Not provided'}
              />
            ) : null}
            <SummaryFieldKS
              heading="Fiscal Year End"
              value={
                data?.requestData?.fiscalMonth
                  ? (() => {
                      const startMonth = Number(data.requestData.fiscalMonth);
                      const endMonth = startMonth === 1 ? 12 : startMonth - 1;
                      return NonTimezoneSensitiveDate.getMonthNames()[
                        endMonth - 1
                      ];
                    })()
                  : null
              }
            />

            <SummaryFieldKS
              heading="Opt in to Commenda's registered address service."
              value={
                data?.requestData?.optForRegisterredAddressService
                  ? 'Yes'
                  : 'No'
              }
            />
            {physicalAddress ? (
              <SummaryFieldKS
                heading="Principal place of business"
                value={[
                  `${physicalAddress?.addressLine1}, ${physicalAddress?.addressLine2 || ''}`,
                  `${physicalAddress?.city} - ${physicalAddress?.postalCode}`,
                  `${
                    State.getStateByCodeAndCountry(
                      physicalAddress?.state,
                      physicalAddress?.country
                    )?.name
                  }, ${
                    Country.getCountryByCode(physicalAddress?.country)?.name
                  }`,
                ]}
              />
            ) : null}
            {data?.requestData?.corporationType === CorporationType.LLC ? (
              <SummaryFieldKS
                heading="LLC Management Type"
                value={
                  data?.requestData?.isMemberManagedOrManagerManaged ===
                  'member-managed'
                    ? 'Member-managed'
                    : 'Manager-managed'
                }
              />
            ) : null}
            {mailingAddress &&
            !data?.requestData?.optForRegisterredAddressService ? (
              <SummaryFieldKS
                heading="Mailing Address"
                value={[
                  `${mailingAddress?.addressLine1}, ${mailingAddress?.addressLine2 || ''}`,
                  `${mailingAddress?.city} - ${mailingAddress?.postalCode}`,
                  `${
                    State.getStateByCodeAndCountry(
                      mailingAddress?.state,
                      mailingAddress?.country
                    )?.name
                  }, ${
                    Country.getCountryByCode(mailingAddress?.country)?.name
                  }`,
                ]}
              />
            ) : null}
          </div>
        </div>
      </div>

      <div className="flex flex-col gap-3">
        <div className="flex items-center justify-between">
          <h4
            className="body-16-bold text-text-primary
          "
          >
            Directors & Officers
          </h4>
          {hideActionButtons ? null : (
            <EditIcon01
              className="cursor-pointer"
              onClick={() => setActiveStep(1)}
            />
          )}
        </div>
        <div className="flex flex-col gap-3 rounded-sm">
          <Accordion
            className="AccordionRoot flex flex-col gap-3"
            type="single"
            collapsible
          >
            {data?.directors
              ?.filter((eachDirector) =>
                eachDirector.corporationHasDirector.find(
                  (chd) =>
                    chd.corporationId === corporationId &&
                    chd.directorId === eachDirector.id &&
                    (chd.additionalDirectorInformation?.isDirector ||
                      shareholderDirectorIds.has(eachDirector.id) ||
                      chd.additionalDirectorInformation?.boardPosition?.length)
                )
              )
              .map((dir) => {
                const passportFile = dir?.directorFiles?.find(
                  (file) => file.documentType === LegacyDocumentType.Passport
                );
                return (
                  <AccordionItem
                    key={dir.id}
                    className="AccordionItem border border-border-primary rounded-md"
                    value={dir.id?.toString()}
                  >
                    <AccordionTrigger className="w-full ">
                      <div className="py-3 px-6 flex justify-between items-center w-full text-text-placeholder">
                        <span className="text-base ">
                          {dir?.directorFirstName} {dir?.directorLastName}
                        </span>
                      </div>
                    </AccordionTrigger>
                    <AccordionContent className="p-4 pt-0">
                      <USFounderField
                        key={dir.id}
                        director={dir}
                        data={data}
                        shareholderDirectorIds={shareholderDirectorIds}
                      />
                    </AccordionContent>
                  </AccordionItem>
                );
              })}
          </Accordion>
        </div>

        <div className="flex flex-col gap-3">
          {individualShareholders.length > 0 && (
            <>
              <div className="flex items-center justify-between">
                <h4 className="body-16-bold text-text-primary">
                  Individual Shareholders
                </h4>
                {hideActionButtons ||
                individualShareholders.length === 0 ? null : (
                  <EditIcon01
                    className="cursor-pointer"
                    onClick={() => setActiveStep(4)}
                  />
                )}
              </div>
              <div className="flex flex-col gap-3 rounded-sm">
                <Accordion
                  className="AccordionRoot flex flex-col gap-3"
                  type="single"
                  collapsible
                >
                  {individualShareholders.map((dir) => (
                    <AccordionItem
                      className="AccordionItem border border-border-primary rounded-md"
                      value={dir.id?.toString()}
                      key={dir.id}
                    >
                      <AccordionTrigger className="w-full ">
                        <div className="py-3 px-6 flex justify-between items-center w-full text-text-placeholder">
                          <span className="text-base ">
                            {dir?.directorFirstName} {dir?.directorLastName}
                          </span>
                        </div>
                      </AccordionTrigger>
                      <AccordionContent className=" p-4 pt-0">
                        <USFounderField
                          key={dir.id}
                          director={dir}
                          data={data}
                          shareholderDirectorIds={shareholderDirectorIds}
                        />
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </div>
            </>
          )}
        </div>

        {extractCorporateShareholders(capTableTransactions).length > 0 && (
          <div className="flex flex-col gap-3">
            <div className="flex items-center justify-between">
              <h4 className="body-16-bold text-text-primary">
                Corporate Shareholders
              </h4>
              {hideActionButtons ? null : (
                <EditIcon01
                  className="cursor-pointer"
                  onClick={() => setActiveStep(4)}
                />
              )}
            </div>
            <CorporateShareholdersSummary
              corporateShareholders={extractCorporateShareholders(
                capTableTransactions
              )}
              directorsData={data?.directors}
            />
          </div>
        )}

        {/* Responsible Party Section */}
        <div className="flex flex-col gap-3">
          <div className="flex items-center justify-between">
            <h4 className="body-16-bold text-text-primary">
              Responsible Party
            </h4>
            {hideActionButtons ? null : (
              <EditIcon01
                className="cursor-pointer"
                onClick={() => setActiveStep(3)}
              />
            )}
          </div>
          <div className="flex flex-col gap-3 rounded-sm">
            <Accordion
              className="AccordionRoot flex flex-col gap-3 bg-bg-disabled-subtle"
              type="single"
              collapsible
            >
              <AccordionItem
                className="AccordionItem border border-border-primary rounded-md bg-bg-disabled-subtle"
                value="responsible-party"
              >
                <AccordionTrigger className="w-full">
                  <div className="py-3 px-6 flex justify-between items-center w-full text-text-placeholder">
                    <span className="text-base">
                      {responsibleParty?.directorFirstName}{' '}
                      {responsibleParty?.directorLastName}
                    </span>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="p-4 pt-0">
                  <USResponsibleParty data={data} />
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </div>
        </div>

        <div className="flex flex-col pt-2 gap-4 mt-4">
          {ownershipSummaryTableData && ownershipSummaryTableData.length > 0 ? (
            <>
              <h3 className="text-base">Ownership Summary</h3>
              <CompanyValuationSummary
                numberOfShares={equityInfo.shareCount}
                sharePrice={equityInfo.price}
                currency={{
                  countryCode: 'en-US',
                  name: 'USD',
                  style: '$',
                }}
                helpTexts={{
                  mainHeading: 'Capital contributions',
                }}
              />
              <CapTable
                data={ownershipSummaryTableData}
                showEsopPool={hasESOPs}
                isNumberOfSharesApplicable={
                  data?.requestData?.corporationType === CorporationType.CCorp
                }
                shareValueCurrency={
                  data?.requestData?.corporationType === CorporationType.CCorp
                    ? 'USD'
                    : undefined
                }
              />
              {data?.requestData?.corporationType === CorporationType.CCorp ? (
                <ShareCapitalSummary
                  authorizedShares={equityInfo.shareCount}
                  pricePerShare={equityInfo.price}
                  capTableData={ownershipSummaryTableData}
                  currency="USD"
                />
              ) : null}
            </>
          ) : (
            <p className="text-text-secondary mt-4">
              No ownership data available.
            </p>
          )}
        </div>
      </div>

      {hideActionButtons ? (
        <Button className="w-fit" onClick={() => router.push('/')}>
          <ProceedIcon className="rotate-180 mr-2 h-4 w-4" />
          <span>Back to Home</span>
        </Button>
      ) : (
        <div className="flex gap-4">
          <Button
            variant="secondary"
            onClick={() => setActiveStep(isCorporateSubsidiary ? 2 : 4)}
          >
            <ProceedIcon className="rotate-180 mr-2 h-4 w-4" />
            <span>Back</span>
          </Button>
          <SubmitWorkRequestModalKS
            isSubmitting={isSubmitting}
            description="This action is irreversible. Once submitted, the details will be
            locked for filing. Please review before submitting."
            onSubmit={onSubmit}
          />
        </div>
      )}
    </div>
  );
};

export default USCorporateStakeholderSummarySection;
